/**
 * Como uma peça da raia eletrônica se apresenta na tela (E78.F1.T06).
 *
 * O caso real GT-2026-0005 tem cinco CCBs e nenhuma traz número: as cinco saíam
 * como "CCB sem número informado", distinguíveis só pelo código de verificação
 * de 64 caracteres. A letra dá ao perito um nome curto para citar a peça, e o
 * nome do arquivo é o que ele reconhece do que enviou.
 */
import { ptBR } from '@/i18n/pt-BR';
import type { ExameEletronicoCcbData, SituacaoItem } from '@/lib/exame_eletronico';

const ALFABETO = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/**
 * Letra da peça pela posição na lista: A, B, … Z, AA, AB…
 *
 * A continuação em duas letras existe porque nada no domínio limita um caso a
 * 26 peças — e uma peça sem letra voltaria a ser indistinguível.
 */
export function letraDaPeca(indice: number): string {
  if (indice < 0) return '';
  let n = indice;
  let letra = '';
  do {
    letra = `${ALFABETO[n % ALFABETO.length] ?? ''}${letra}`;
    n = Math.floor(n / ALFABETO.length) - 1;
  } while (n >= 0);
  return letra;
}

/**
 * O que o documento É, para a tela (E90.F6): o nome dado pelo perito; senão o
 * tipo identificado (com o número da CCB, se houver); `null` quando só resta
 * o número da CCB ou o arquivo — o mesmo degrau do papel (`identificacao`).
 */
export function nomeDoDocumento(ccb: ExameEletronicoCcbData): string | null {
  const editado = ccb.nome_do_documento?.trim() ?? '';
  if (editado !== '') return editado;
  const tipo = ccb.tipo_da_peca ?? null;
  if (tipo === null || tipo === 'outro') return null;
  const rotulo = ptBR.exameEletronico.tabelaDePecas.tipoDaPeca[tipo];
  const numero = ccb.ccb_number ?? '';
  return numero === '' ? rotulo : `${rotulo} ${numero}`;
}

/**
 * A letra que a peça carrega (E106.F3.C11): a que o servidor emitiu pela regra do documento
 * emitido — o comprovante separado entra na sequência, e a fotografia fica sem letra
 * (`null`). Só um payload anterior ao campo cai na posição da lista, que era a regra da
 * tela e chamava o laudo de "C" enquanto o documento chamava assim o comprovante.
 */
export function letraDaPecaNaTela(ccb: ExameEletronicoCcbData, indice: number): string | null {
  return ccb.letra === undefined ? letraDaPeca(indice) : ccb.letra;
}

function comLetra(letra: string | null, arquivo: string): string {
  if (letra === null) return ptBR.exameEletronico.pecaSemLetra.replace('{arquivo}', arquivo);
  return ptBR.exameEletronico.pecaSemNumero.replace('{letra}', letra).replace('{arquivo}', arquivo);
}

/**
 * Título da peça: letra + o que o documento É (nome ou tipo); sem os dois, o
 * número da CCB quando existe; senão letra + arquivo. Sem letra (fotografia),
 * só o que o documento é.
 */
export function tituloDaPeca(ccb: ExameEletronicoCcbData, indice: number): string {
  const letra = letraDaPecaNaTela(ccb, indice);
  const nome = nomeDoDocumento(ccb);
  if (nome !== null) return comLetra(letra, nome);
  if (ccb.ccb_number !== null && ccb.ccb_number !== '') {
    return ptBR.exameEletronico.ccbTitulo.replace('{numero}', ccb.ccb_number);
  }
  return comLetra(letra, ccb.original_filename);
}

/** Mesma régua do servidor: 64 hex, espaços, dois-pontos e maiúsculas normalizados. */
const SHA256_HEX = /^[0-9a-f]{64}$/;

export function normalizarCodigo(valor: string): string {
  return valor.replace(/[\s:]/g, '').toLowerCase();
}

/** Vazio é válido (a peça pode não declarar código); preenchido tem de ser 64 hex. */
export function codigoValido(valor: string): boolean {
  const normalizado = normalizarCodigo(valor);
  return normalizado.length === 0 || SHA256_HEX.test(normalizado);
}

/**
 * O confronto entre o código declarado e o apurado, LIDO do quadro.
 *
 * Nunca recalculado na tela: quem confronta é o motor, e o item `hash_ccb` é o
 * veredito dele. Sem exame verificado a coluna fica pendente — e não "confere".
 */
export function confrontoDaPeca(ccb: ExameEletronicoCcbData): SituacaoItem | null {
  const item = ccb.exame?.resultado?.integridade.find((i) => i.chave === 'hash_ccb');
  return item?.situacao ?? null;
}

/** Quantos indicadores de alerta o exame desta peça apurou. */
export function alertasDaPeca(ccb: ExameEletronicoCcbData): number | null {
  return ccb.exame?.resultado?.red_flags.length ?? null;
}
